import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Loader2, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { Helmet } from 'react-helmet';
import { useStructuredData } from '../hooks/useStructuredData';

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState(null);
  const [trialDays, setTrialDays] = useState(15);
  const [openFaq, setOpenFaq] = useState(null);

  useStructuredData('/pricing');
  
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    fetchPlans();
    api.get('/trial/config')
      .then((res) => setTrialDays(res.data.trial_days))
      .catch(() => {});
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/stripe/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planType) => {
    navigate(`/checkout?plan=${planType}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Pricing | SlickSignature</title>
        <meta name="description" content="Simple pricing for team-wide email signatures: $9/month for a single user, $59/month for unlimited team members, with a free trial." />
        <link rel="canonical" href="https://slicksignature.com/pricing" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SlickSignature" />
        <meta property="og:url" content="https://slicksignature.com/pricing" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Header */}
      <header className="bg-black text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" className="cursor-pointer">
                <img 
                  src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                  alt="SlickSignature"
                  className="h-12 hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                  }}
                />
              </a>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="text-black bg-white hover:bg-gray-100"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          Professional Email Signatures Made Simple
        </h1>
        <p className="text-xl text-gray-600 mb-4 max-w-3xl mx-auto">
          Create stunning, consistent email signatures for your entire team in minutes.
          Start your {trialDays}-day free trial today — credit card required, cancel anytime before it ends.
        </p>
        <p className="text-sm text-gray-500">
          You won't be charged until the trial ends. Cancel anytime during the trial period.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Personal Plan */}
          {plans?.personal && (
            <Card className="relative border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-3xl font-bold mb-2">Personal</CardTitle>
                <CardDescription className="text-lg">Perfect for solopreneurs</CardDescription>
                <div className="mt-6">
                  <span className="text-5xl font-bold text-gray-900">${plans.personal.price}</span>
                  <span className="text-gray-600 text-lg">/month</span>
                </div>
                <p className="text-sm text-green-600 font-semibold mt-2">{trialDays}-day free trial included</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <Button
                  onClick={() => handleSelectPlan('personal')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg"
                >
                  Start Free Trial
                </Button>
                
                <div className="space-y-3">
                  <p className="font-semibold text-gray-900 text-sm uppercase tracking-wide">What's included:</p>
                  {plans.personal.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Business Plan */}
          {plans?.business && (
            <Card className="relative border-2 border-primary shadow-xl hover:shadow-2xl transition-shadow">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-3xl font-bold mb-2">Business</CardTitle>
                <CardDescription className="text-lg">For growing teams</CardDescription>
                <div className="mt-6">
                  <span className="text-5xl font-bold text-gray-900">${plans.business.price}</span>
                  <span className="text-gray-600 text-lg">/month</span>
                </div>
                <p className="text-sm text-green-600 font-semibold mt-2">{trialDays}-day free trial included</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <Button
                  onClick={() => handleSelectPlan('business')}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg"
                >
                  Start Free Trial
                </Button>
                
                <div className="space-y-3">
                  <p className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Everything in Personal, plus:</p>
                  {plans.business.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Compare Plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Feature</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Personal</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Business</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-gray-700">Number of Users</td>
                  <td className="py-4 px-6 text-center">1</td>
                  <td className="py-4 px-6 text-center">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-6 text-gray-700">Office Locations</td>
                  <td className="py-4 px-6 text-center">1</td>
                  <td className="py-4 px-6 text-center">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-gray-700">White Label Branding</td>
                  <td className="py-4 px-6 text-center">—</td>
                  <td className="py-4 px-6 text-center"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-6 text-gray-700">Field Visibility Controls</td>
                  <td className="py-4 px-6 text-center">Basic</td>
                  <td className="py-4 px-6 text-center">Advanced</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-gray-700">Custom Brand Colors</td>
                  <td className="py-4 px-6 text-center"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-6 text-gray-700">Priority Support</td>
                  <td className="py-4 px-6 text-center">—</td>
                  <td className="py-4 px-6 text-center"><Check className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <Card className="border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleFaq(0)}
                className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex justify-between items-center"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">How does the free trial work?</h3>
                <ChevronDown 
                  className={`h-6 w-6 text-gray-400 flex-shrink-0 transition-transform ${
                    openFaq === 0 ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              {openFaq === 0 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    Start with a {trialDays}-day free trial. A credit card is required to start, but you won't be charged until the trial ends. Cancel anytime during the trial with no charge.
                  </p>
                </div>
              )}
            </Card>

            <Card className="border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleFaq(1)}
                className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex justify-between items-center"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">Can I change plans later?</h3>
                <ChevronDown 
                  className={`h-6 w-6 text-gray-400 flex-shrink-0 transition-transform ${
                    openFaq === 1 ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              {openFaq === 1 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    Yes! You can upgrade or downgrade your plan at any time. When upgrading, you'll be charged a prorated amount for the remainder of your billing period.
                  </p>
                </div>
              )}
            </Card>

            <Card className="border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleFaq(2)}
                className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex justify-between items-center"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">What happens if I cancel?</h3>
                <ChevronDown 
                  className={`h-6 w-6 text-gray-400 flex-shrink-0 transition-transform ${
                    openFaq === 2 ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              {openFaq === 2 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    You'll have 30 days to reactivate your account with all your data intact. After 30 days, your account and data will be permanently deleted.
                  </p>
                </div>
              )}
            </Card>

            <Card className="border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleFaq(3)}
                className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex justify-between items-center"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">Is my payment information secure?</h3>
                <ChevronDown 
                  className={`h-6 w-6 text-gray-400 flex-shrink-0 transition-transform ${
                    openFaq === 3 ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              {openFaq === 3 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    Absolutely. We use Stripe for payment processing, which is PCI-DSS Level 1 certified. We never store your credit card information.
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-400">© {new Date().getFullYear()} SlickSignature. All rights reserved.</p>
            <div className="mt-4 space-x-6">
              <a href="#" className="text-gray-400 hover:text-white">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-white">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
